/**
 * RS-22 follow-up: province codes (BGD, KRB, BAS…) are an internal API
 * concept that mean nothing to a non-technical merchant. The UI must
 * always show the real province name (English title-cased OR Arabic) but
 * still submit the canonical province_code so it matches order data on
 * the backend.
 */

// Loose shape — accepts both RegionItem (where some fields are non-null)
// and the raw API row (where province_en_name can be null in faker rows).
interface ProvinceRow {
  province_code?: string | null;
  province_en_name?: string | null;
  province?: string | null; // Arabic
}

export interface ProvinceOption {
  value: string; // province_code (what we submit)
  label: string; // localized display name
}

const isLikelyRealProvince = (row: ProvinceRow): boolean => {
  // The regions table in staging has ~18 real Iraqi provinces and ~600+
  // faker-generated rows from seed data. A real province has both a code
  // AND a non-null english/arabic name. Faker rows typically have null
  // province_en_name and a fake city as province_code.
  if (!row.province_code) return false;
  if (!row.province_en_name && !row.province) return false;
  return true;
};

const titleCase = (s: string): string =>
  s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');

const localizedName = (row: ProvinceRow, locale: 'en' | 'ar'): string => {
  if (locale === 'ar') {
    return (row.province ?? '').trim() || titleCase(row.province_en_name ?? '');
  }
  return titleCase((row.province_en_name ?? '').trim());
};

/**
 * Build a deduped, name-sorted list of province options for a select.
 * Filters out faker/garbage rows.
 */
export function buildProvinceOptions(
  rows: ProvinceRow[],
  locale: 'en' | 'ar' = 'en',
): ProvinceOption[] {
  const seen = new Map<string, ProvinceOption>();
  for (const r of rows) {
    if (!isLikelyRealProvince(r)) continue;
    const code = r.province_code as string;
    if (seen.has(code)) continue;
    const label = localizedName(r, locale);
    if (!label) continue;
    seen.set(code, { value: code, label });
  }
  return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Look up a province display name from a code. Returns the original code
 * unchanged when the province is unknown so the UI never shows blank.
 */
export function resolveProvinceName(
  code: string | undefined,
  rows: ProvinceRow[] = [],
  locale: 'en' | 'ar' = 'en',
): string {
  if (!code) return '';
  for (const r of rows) {
    if (r.province_code === code) {
      const name = localizedName(r, locale);
      return name || code;
    }
  }
  return code;
}
